import React, { useState, useEffect } from 'react';
import { message, Form, Input, InputNumber, Checkbox } from 'antd';
import Card, { CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import { EMDASH } from 'constants';
import { FAMILY_BRANCH_ARRAY } from 'containers/Family/AddApplication2/familyApplicationConstants';
import providerService from 'services/providers.service';
import RadioCards from 'components/RadioCards/RadioCards';

const FamilyInformation = ({ application, isEditable = false }) => {
  const [localApplication, setLocalApplication] = useState(application);
  const [familyInfoForm] = Form.useForm();
  const applyingForSpecificFamily = Form.useWatch('applyingForSpecificFamily', familyInfoForm);
  const branchUnknown = Form.useWatch('branchUnknown', familyInfoForm);

  // Sync localApplication when application prop changes (e.g., route change)
  useEffect(() => {
    setLocalApplication(application);
  }, [application]);

  const currentApplication = localApplication || application;
  const providerFamilyInfo = currentApplication?.providerFamilyInfo;
  const hasProviderFamilyInfo = !!providerFamilyInfo;

  // Find the branch label from FAMILY_BRANCH_ARRAY
  const getBranchLabel = () => {
    if (providerFamilyInfo?.branchUnknown) {
      return "I don't know right now";
    }
    if (providerFamilyInfo?.branch?.id) {
      const branch = FAMILY_BRANCH_ARRAY.find((b) => b.id === providerFamilyInfo.branch.id);
      return branch?.title || EMDASH;
    }
    return EMDASH;
  };

  const { isEditing, editProps } = useEditCard({
    id: 'family-information',
    onEditStart: () =>
      familyInfoForm.setFieldsValue({
        applyingForSpecificFamily: hasProviderFamilyInfo,
        familyName: providerFamilyInfo?.familyName,
        familyId: providerFamilyInfo?.familyId,
        militaryBranch: providerFamilyInfo?.branch?.id,
        branchUnknown: providerFamilyInfo?.branchUnknown,
      }),
    onSave: async () => {
      await familyInfoForm.validateFields();
      const values = familyInfoForm.getFieldsValue();

      const payload = {
        ...currentApplication,
      };

      if (values.applyingForSpecificFamily) {
        payload.providerFamilyInfo = {
          ...(currentApplication.providerFamilyInfo?.id && { id: currentApplication.providerFamilyInfo.id }),
          familyName: values.familyName || null,
          familyId: values.familyId ? Number(values.familyId) : null,
          branch:
            values.militaryBranch && values.branchUnknown !== true
              ? {
                  id: values.militaryBranch,
                }
              : null,
          branchUnknown: values.branchUnknown || null,
        };
      } else {
        payload.providerFamilyInfo = null;
      }

      const updatedApplication = await providerService.updateApplication(currentApplication.id, payload);
      setLocalApplication(updatedApplication);
      familyInfoForm.resetFields();
      message.success('Family information updated');
    },
    onDiscard: () => familyInfoForm.resetFields(),
  });

  return (
    <Card title="Family information" extra={isEditable && <CardEdit {...editProps} />} noBodyPadding stickyHeader>
      <Form form={familyInfoForm} disabled={editProps.loading}>
        <TabularList
          indented
          data={[
            {
              title: 'Are you applying to this program for a specific military family?',
              titleRequired: true,
              value: isEditing ? (
                <Form.Item
                  name="applyingForSpecificFamily"
                  className="mb-0"
                  rules={[{ required: true, message: 'Please select an option' }]}
                >
                  <RadioCards
                    size="small"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                  />
                </Form.Item>
              ) : hasProviderFamilyInfo ? (
                'Yes'
              ) : (
                'No'
              ),
            },
            ...(applyingForSpecificFamily || (!isEditing && hasProviderFamilyInfo)
              ? [
                  {
                    title: 'Military sponsor or child first and last name',
                    titleRequired: true,
                    value: isEditing ? (
                      <Form.Item
                        name="familyName"
                        className="mb-0"
                        rules={[{ required: true, message: 'Please enter the family name' }]}
                      >
                        <Input placeholder="Enter name" />
                      </Form.Item>
                    ) : (
                      providerFamilyInfo.familyName || EMDASH
                    ),
                  },
                  {
                    title: 'Family ID#',
                    titleRequired: true,
                    value: isEditing ? (
                      <Form.Item
                        name="familyId"
                        className="mb-0"
                        rules={[{ required: true, message: 'Please enter the family ID' }]}
                      >
                        <InputNumber placeholder="Enter family ID" className="w-full" controls={false} />
                      </Form.Item>
                    ) : (
                      providerFamilyInfo.familyId || EMDASH
                    ),
                  },
                  ...(isEditing
                    ? [
                        {
                          title: '',
                          value: (
                            <Form.Item name="branchUnknown" valuePropName="checked" className="mb-0">
                              <Checkbox>I don't know the military branch right now</Checkbox>
                            </Form.Item>
                          ),
                        },
                      ]
                    : []),
                  ...((isEditing && !branchUnknown) || (!isEditing && !providerFamilyInfo?.branchUnknown)
                    ? [
                        {
                          title: 'Military Branch',
                          titleRequired: true,
                          value: isEditing ? (
                            <Form.Item
                              name="militaryBranch"
                              className="mb-0"
                              rules={[{ required: true, message: 'Please select a military branch' }]}
                            >
                              <RadioCards
                                size="small"
                                options={FAMILY_BRANCH_ARRAY.map((branch) => ({
                                  value: branch.id,
                                  label: branch.title,
                                }))}
                              />
                            </Form.Item>
                          ) : (
                            getBranchLabel()
                          ),
                        },
                      ]
                    : isEditing
                    ? []
                    : [
                        {
                          title: 'Military Branch',
                          value: getBranchLabel(),
                        },
                      ]),
                ]
              : []),
          ]}
        />
      </Form>
    </Card>
  );
};

export default FamilyInformation;
